import React, { useState } from 'react'
import Link from 'next/link'
import { useFindMenu, useTrans, useTealium } from '@next-nx/hooks'
import { getIcon } from '@next-nx/utils/icons'
import { getTimestamp } from '@next-nx/utils'
import { Logo, LogoGruppo } from '@next-nx/shared-ui/icons'
import { NumeroVerde, WContainer, WLink } from '@next-nx/shared-ui/atoms'
import { NewsletterBox, NewsletterThankYouModal } from '@next-nx/shared-ui/organisms'
import styles from './Footer.module.sass'

interface Props {
  className?: string
}

const Footer = (props: Props) => {
  const { className = '' } = props

  const t = useTrans()

  const [openNewsletterThankYouModal, setOpenNewsletterThankYouModal] = useState(false)
  const [statusNewsletterThankYouModal, setStatusNewsletterThankYouModal] = useState<
    string | undefined
  >()

  const footerMenu = useFindMenu('footer')
  const socialMenu = useFindMenu('social')
  const { linkDataLayer } = useTealium()

  const handleClick = (action_type = '', cta = '-', isSocial = false, href = '') => {
    const action_timestamp = getTimestamp()

    if (isSocial && !!href.length) {
      if (href.includes('facebook')) {
        action_type = 'seguici_facebook'
      } else if (href.includes('linkedin')) {
        action_type = 'seguici_linkedin'
      } else if (href.includes('youtube')) {
        action_type = 'seguici_youtube'
      } else if (href.includes('instagram')) {
        action_type = 'seguici_instagram'
      }
    }

    linkDataLayer(
      !isSocial
        ? {
            virtual: 'action',
            action_name: 'navigazione',
            action_tech: 'click',
            action_type: action_type.replace(/ /g, '_').toLocaleLowerCase(),
            action_timestamp,
            action_effect: 'navigazione_footer',
            cta: cta.replace(/ /g, '_').toLowerCase(),
          }
        : {
            virtual: 'action',
            action_name: 'social',
            action_tech: 'click',
            action_type,
            action_timestamp,
            action_effect: 'follow_social',
          }
    )
  }

  return (
    <>
      <NewsletterBox
        onSubmit={(status) => {
          setOpenNewsletterThankYouModal(true)
          setStatusNewsletterThankYouModal(status)
        }}
      />
      <NewsletterThankYouModal
        open={openNewsletterThankYouModal}
        setOpen={setOpenNewsletterThankYouModal}
        status={statusNewsletterThankYouModal}
      />
      <footer className={`${styles.footer} ${className}`}>
        <WContainer>
          <div className={styles.layout}>
            <div className={styles.top}>
              <div className={styles.logoWrap}>
                <Link href="/">
                  <a className={styles.logo}>
                    <Logo />
                  </a>
                </Link>
              </div>
            </div>
            <div className={styles.menu}>
              {footerMenu?.children?.edges
                ?.map((item) => item.node)
                .map((item) => (
                  <div
                    key={item.pk}
                    className={`${styles.menuItem} ${
                      item.key ? styles[`menuItem--${item.key}`] : ''
                    } ${item.children?.edges ? styles['menuItem--mobile-wrap'] : ''}`}
                  >
                    {!!item.url && (
                      <WLink
                        href={item.url}
                        key={item.pk}
                        className={styles.menuItemMain}
                        newPage={item.blank}
                        onClick={() => handleClick(item.name)}
                        unoptimized={item.file ? true : false}
                        file={item.file}
                      >
                        {item.name}
                      </WLink>
                    )}
                    {!!item.children?.edges?.length && (
                      <div className={styles.menuSubmenu}>
                        {item.children.edges
                          .map((x) => x.node)
                          .map(
                            (x) =>
                              !!x.url && (
                                <WLink
                                  href={
                                    x.key != 'cookie-consent'
                                      ? x.url
                                      : 'javascript: Cookiebot.renew()'
                                  }
                                  key={x.pk}
                                  className={styles.menuSubitem}
                                  newPage={x.blank}
                                  onClick={() => handleClick(item.name, x.name)}
                                  unoptimized={x.file ? true : false}
                                  file={x.file}
                                >
                                  {x.name}
                                </WLink>
                              )
                          )}
                      </div>
                    )}
                  </div>
                ))}
            </div>
            <div className={styles.phoneBox}>
              <NumeroVerde
                id="numero-verde"
                phoneLabel={t('Numero Verde')}
                description={t(
                  'Hai bisogno del soccorso stradale?<br />Chiama il numero verde gratuito'
                )}
              />
            </div>
            <div className={styles.bottom}>
              {!!socialMenu?.children?.edges?.length && (
                <div className={styles.social}>
                  <div className={styles.socialLabel}>{socialMenu.name}</div>
                  <div className={styles.socialMenu}>
                    {socialMenu?.children?.edges
                      ?.map((item) => item.node)
                      .map((item) => (
                        <a
                          href={item.url}
                          target="_blank"
                          className={styles.socialItem}
                          key={item.pk}
                          onClick={() => handleClick('', '', true, item.url)}
                        >
                          {getIcon(item.style)}
                        </a>
                      ))}
                  </div>
                </div>
              )}
              <div className={styles.copyright}>{t('footer_copyright')}</div>
              <div className={styles.groupLogo}>
                <LogoGruppo />
              </div>
            </div>
          </div>
        </WContainer>
      </footer>
    </>
  )
}

export default Footer
